import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { interactionsAPI } from "../services/api";
import DataTable from "../components/DataTable";
import InteractionModal from "../components/InteractionModal";
import SearchableSelect from "../components/SearchableSelect";

const METHOD_FILTER_OPTIONS = [
  { value: "Visited",    label: "Visited" },
  { value: "Phone Call", label: "Phone Call" },
  { value: "Message",    label: "Message" },
  { value: "Video Call", label: "Video Call" },
  { value: "Meeting",    label: "Meeting" },
];

const DATE_RANGE_OPTIONS = [
  { value: "today",   label: "Today" },
  { value: "week",    label: "Last 7 Days" },
  { value: "month",   label: "Last 30 Days" },
  { value: "quarter", label: "Last 90 Days" },
];

const METHOD_BADGE = {
  Visited:      "badge-success",
  "Phone Call": "badge-info",
  Message:      "badge-warning",
  "Video Call": "badge-info",
  Meeting:      "badge-gray",
};

const getLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Parse YYYY-MM-DD as local midnight so date comparisons are timezone-safe
const parseDateLocal = (str) => {
  if (!str) return null;
  const s = String(str).slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const Interactions = () => {
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [contactMethod, setContactMethod] = useState("");
  const [dateRange, setDateRange]         = useState("");
  const [showInteractionModal, setShowInteractionModal] = useState(false);

  /* ── data ────────────────────────────────────────────────────────── */
  const fetchInteractions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await interactionsAPI.getAll();
      setInteractions(response.data);
    } catch (err) {
      setError("Failed to load interactions");
      console.error("Interactions error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInteractions(); }, [fetchInteractions]);

  /* ── client-side filtering ───────────────────────────────────────── */
  const filtered = useMemo(() => {
    let result = [...interactions];

    if (contactMethod) {
      result = result.filter(i => i.contact_method === contactMethod);
    }

    if (dateRange) {
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);

      if (dateRange === "today") {
        const todayStr = getLocalDateString();
        result = result.filter(i => i.contact_date && String(i.contact_date).slice(0, 10) === todayStr);
      } else {
        const days = { week: 7, month: 30, quarter: 90 }[dateRange];
        if (days) {
          const cutoff = new Date(todayMidnight);
          cutoff.setDate(cutoff.getDate() - days);
          result = result.filter(i => {
            const d = parseDateLocal(i.contact_date);
            return d && d >= cutoff;
          });
        }
      }
    }

    return result;
  }, [interactions, contactMethod, dateRange]);

  /* ── helpers ─────────────────────────────────────────────────────── */
  const formatDate = (d) => {
    const parsed = parseDateLocal(d);
    return parsed ? parsed.toLocaleDateString("en-KE") : "-";
  };
  const formatDateTime = (d) => d ? new Date(d).toLocaleString("en-KE") : "-";

  /* ── columns ─────────────────────────────────────────────────────── */
  const columns = [
    {
      key: "_no", label: "#",
      sortable: false, export: false,
      thClass: "w-12",
      render: (_, __, idx) => (
        <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
      ),
    },
    {
      key: "customer_name", label: "Customer",
      exportValue: r => r.customer_name,
      render: (val, row) => (
        <Link
          to={`/customers/${row.customer_id}`}
          className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
        >{val}</Link>
      ),
    },
    {
      key: "contact_number", label: "Phone",
      exportValue: r => r.contact_number || "",
      render: val => val || <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: "contact_method", label: "Method",
      exportValue: r => r.contact_method,
      render: val => (
        <span className={`badge text-xs ${METHOD_BADGE[val] || "badge-secondary"}`}>
          {val}
        </span>
      ),
    },
    {
      key: "contact_date", label: "Date",
      exportValue: r => formatDate(r.contact_date),
      render: (val, row) => (
        <div className="text-sm">
          <div>{formatDate(val)}</div>
          {row.scheduled_time && (
            <div className="text-xs text-gray-500">at {row.scheduled_time}</div>
          )}
        </div>
      ),
    },
    {
      key: "is_scheduled", label: "Type",
      exportValue: r => r.is_scheduled === 1 ? "Scheduled" : "Walk-in",
      render: val => val === 1
        ? <span className="badge badge-warning text-xs">Scheduled</span>
        : <span className="badge badge-gray text-xs">Walk-in</span>,
    },
    {
      key: "notes", label: "Notes",
      exportValue: r => r.notes || "",
      tdClass: "max-w-xs",
      render: val => val
        ? <span className="text-gray-700 text-xs line-clamp-2">{val}</span>
        : <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: "created_by_name", label: "Logged By",
      exportValue: r => r.created_by_name || "",
    },
    {
      key: "created_at", label: "Logged At",
      exportValue: r => formatDateTime(r.created_at),
      render: val => (
        <span className="text-xs text-gray-500">{formatDateTime(val)}</span>
      ),
    },
    {
      key: "actions", label: "",
      sortable: false, export: false,
      render: (_, row) => (
        <Link
          to={`/customers/${row.customer_id}`}
          className="btn btn-secondary text-xs py-1 px-2.5 whitespace-nowrap"
        >
          View Customer
        </Link>
      ),
    },
  ];

  const searchFields = [
    "customer_name", "contact_number", "notes",
    "created_by_name", "contact_method",
  ];

  const resetFilters = () => { setContactMethod(""); setDateRange(""); };

  /* ── render ──────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Interactions</h1>
          <p className="mt-1 text-sm text-gray-600">
            View and search all customer interactions
          </p>
        </div>
        <button
          onClick={() => setShowInteractionModal(true)}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Interaction
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchInteractions} className="underline text-sm ml-4">Retry</button>
        </div>
      )}

      {/* DataTable Card */}
      <div className="card">
        <div className="card-body">
          <DataTable
            key={`${contactMethod}-${dateRange}`}
            data={filtered}
            columns={columns}
            searchFields={searchFields}
            filename="interactions"
            title="Interactions Report"
            loading={loading}
            rowKey={row => row.id}
          >
            {/* Extra filters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-3 border-b border-gray-100 mb-2">
              <div>
                <label className="form-label text-xs">Contact Method</label>
                <SearchableSelect
                  value={contactMethod}
                  onChange={setContactMethod}
                  options={METHOD_FILTER_OPTIONS}
                  placeholder="All Methods"
                />
              </div>
              <div>
                <label className="form-label text-xs">Date Range</label>
                <SearchableSelect
                  value={dateRange}
                  onChange={setDateRange}
                  options={DATE_RANGE_OPTIONS}
                  placeholder="All Time"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="btn btn-secondary text-sm inline-flex items-center gap-1.5 w-full sm:w-auto justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset Filters
                </button>
              </div>
            </div>

            {/* Summary strip */}
            {!loading && (
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 pb-3">
                <span>
                  <span className="font-semibold text-gray-800">{interactions.length}</span> total interactions
                </span>
                {(contactMethod || dateRange) && (
                  <span>
                    <span className="font-semibold text-primary-600">{filtered.length}</span> matching filters
                  </span>
                )}
              </div>
            )}
          </DataTable>
        </div>
      </div>

      {showInteractionModal && (
        <InteractionModal
          onClose={() => {
            setShowInteractionModal(false);
            fetchInteractions();
          }}
          onSuccess={fetchInteractions}
        />
      )}
    </div>
  );
};

export default Interactions;
